import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, LogOut, BookMarked, Search as SearchIcon, Settings as Cog } from "lucide-react";

/**
 * Minimal chrome — brand, top nav (collections / search / settings), avatar,
 * logout, plus the persistent Add FAB. The room grid itself is the primary
 * navigation on the Hall; these three top links are the always-there
 * escape hatches you'd expect on every screen.
 */
const topLinks = [
  { to: "/collections", label: "collections", Icon: BookMarked,  testId: "top-nav-collections" },
  { to: "/search",      label: "search",      Icon: SearchIcon,  testId: "top-nav-search" },
  { to: "/settings",    label: "settings",    Icon: Cog,         testId: "top-nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const hideFab = loc.pathname.startsWith("/add");

  return (
    <div className="paper-grain min-h-screen relative">
      <header className="relative z-10">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-link" className="flex items-baseline gap-2 shrink-0">
            <span className="font-mono-lc text-[10px] tracking-[0.28em] uppercase text-[var(--brass)] opacity-80">
              curated
            </span>
            <span className="font-serif-lit italic text-2xl text-[var(--cream)] -translate-y-[1px]">
              ethos
            </span>
          </Link>

          <nav className="flex items-center gap-1 sm:gap-2" aria-label="global">
            {topLinks.map(({ to, label, Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testId}
                title={label}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full border transition-colors ` +
                  (isActive
                    ? "border-[var(--brass)] text-[var(--brass)] bg-[var(--brass-glow)]"
                    : "border-[rgba(236,225,205,0.14)] text-[var(--cream-dim)] hover:border-[var(--brass)] hover:text-[var(--brass)]")
                }
              >
                <Icon size={12} strokeWidth={1.6} />
                <span className="hidden sm:inline font-mono-lc text-[10px] tracking-[0.16em] uppercase">
                  {label}
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {user?.picture && (
              <img
                src={user.picture}
                alt=""
                className="w-8 h-8 rounded-full border border-[rgba(179,137,90,0.32)] object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            {user && (
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
                title="sign out"
              >
                <LogOut size={16} strokeWidth={1.4} />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 pb-24 md:pb-16">{children}</main>

      {!hideFab && (
        <Link to="/add" data-testid="add-fab" className="fab" title="add">
          <Plus size={22} strokeWidth={2} />
        </Link>
      )}
    </div>
  );
}
